/**
 * SPA Router
 * 
 * SPA Constitution Compliant:
 * - Uses History API (pushState, popstate)
 * - Navigation does NOT reload the page
 * - Back/forward buttons work
 * - Direct URL access works
 */

// NOTE: `normalizeAppPath` strips the base path (e.g. '/parler/login' -> '/login').
// The router stores routes under their full base-prefixed keys (registered via
// APP_ROUTES, e.g. '/parler/login') and the browser gives us `window.location.pathname`
// with the base prefix intact. Stripping the base here breaks route lookup and would
// also push bare paths like '/login' into history, breaking nginx routing. Keep the
// path as-is so registered keys match incoming navigation paths and pushState targets.

class Router {
  constructor() {
    this.routes = {};
    this.currentPage = null;
    this.currentRoute = null;
    this.mountPoint = null;
    this.beforeNavigate = null;
  }

  normalizePath(path) {
    return path;
  }

  /**
   * Initialize the router
   * @param {string} mountPointSelector - CSS selector for mount point
   */
  init(mountPointSelector = '#app') {
    this.mountPoint = document.querySelector(mountPointSelector);

    // Router Integrity Law: single navigation authority.
    // Expose a stable reference for modules that need navigation without bypassing the router.
    if (typeof window !== 'undefined') {
      window.__parlerRouter = this;
    }
    
    // Handle browser back/forward
    window.addEventListener('popstate', () => {
      this.navigate(window.location.pathname, false);
    });

    // Handle link clicks
    document.addEventListener('click', (event) => {
      const anchor = event.target.closest('a');
      if (!anchor) return;
      
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) {
        return;
      }

      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;

      event.preventDefault();
      this.navigate(url.pathname);
    });

    // Initial navigation
    this.navigate(window.location.pathname, false);
  }

  /**
   * Register a route
   * @param {string} path - Route path (e.g., '/', '/login', '/products/:id')
   * @param {Function} pageLoader - Async function that returns page module
   */
  register(path, pageLoader) {
    this.routes[path] = pageLoader;
  }

  /**
   * Register an optional navigation guard.
   * Guard signature: ({ path, pushState }) => null | false | { redirectTo: string }
   */
  setBeforeNavigate(guardFn) {
    this.beforeNavigate = typeof guardFn === 'function' ? guardFn : null;
  }

  /**
   * Navigate to a route
   * @param {string} path - Path to navigate to
   * @param {boolean} pushState - Whether to push state to history
   */
  async navigate(path, pushState = true) {
    const normalizedPath = this.normalizePath(path);

    if (typeof this.beforeNavigate === 'function') {
      try {
        const decision = await this.beforeNavigate({ path: normalizedPath, pushState });
        if (decision === false) return;
        if (decision && typeof decision === 'object' && decision.redirectTo) {
          const redirectPath = this.normalizePath(String(decision.redirectTo));
          if (redirectPath !== normalizedPath) {
            await this.navigate(redirectPath, pushState);
            return;
          }
        }
      } catch (error) {
        console.error('Navigation guard error:', error);
      }
    }

    // Unmount current page
    if (this.currentPage && typeof this.currentPage.unmount === 'function') {
      this.currentPage.unmount();
    }

    // Find matching route
    const { route, params } = this.matchRoute(normalizedPath);
    
    if (!route) {
      this.renderNotFound();
      return;
    }

    // Update history
    if (pushState) {
      history.pushState({ path: normalizedPath }, '', normalizedPath);
    }

    try {
      // Load page module
      const pageModule = await this.routes[route]();
      const PageComponent = pageModule.default || Object.values(pageModule)[0];
      
      // Create page instance
      let page;
      if (typeof PageComponent === 'function') {
        try {
          page = new PageComponent(params);
        } catch (error) {
          page = PageComponent(params);
        }
      } else {
        page = PageComponent;
      }
      this.currentPage = page;
      this.currentRoute = route;

      // Render page
      if (this.mountPoint) {
        this.mountPoint.innerHTML = page.render();
      }

      // Mount page (attach event listeners)
      if (typeof page.mount === 'function') {
        page.mount();
      }

      // SEO system disabled for performance
      // applyPageSEO(normalizedPath);
    } catch (error) {
      console.error('Navigation error:', error);
      this.renderError(error);
    }
  }

  /**
   * Match path to registered route
   * @param {string} path - Path to match
   * @returns {{ route: string|null, params: object }}
   */
  matchRoute(path) {
    // Exact match first
    if (this.routes[path]) {
      return { route: path, params: {} };
    }

    // Parameter matching
    for (const route of Object.keys(this.routes)) {
      const paramNames = [];
      const regexPath = route.replace(/:([^/]+)/g, (_, paramName) => {
        paramNames.push(paramName);
        return '([^/]+)';
      });

      const regex = new RegExp(`^${regexPath}$`);
      const match = path.match(regex);

      if (match) {
        const params = {};
        paramNames.forEach((name, index) => {
          params[name] = match[index + 1];
        });
        return { route, params };
      }
    }

    return { route: null, params: {} };
  }

  /**
   * Render 404 page
   */
  renderNotFound() {
    if (this.mountPoint) {
      this.mountPoint.innerHTML = `
        <div class="error-page">
          <h1>404</h1>
          <p>Page not found</p>
          <a href="/">Go home</a>
        </div>
      `;
    }
  }

  /**
   * Render error page
   * @param {Error} error 
   */
  renderError(error) {
    if (this.mountPoint) {
      this.mountPoint.innerHTML = `
        <div class="error-page">
          <h1>Error</h1>
          <p>${error.message}</p>
          <a href="/">Go home</a>
        </div>
      `;
    }
  }
}

export const router = new Router();
export { Router };
